'use client';

import React, { useEffect } from 'react';
import { componentByTypename } from '@/ui/utils/componentByTypename';
import { parseComponents } from '@/ui/utils/tiles';
import { twJoin } from 'tailwind-merge';
import { BentoTile } from './types/BentoTile';
import Container from '../../shared/Container';
import ContentBentoBox from './components/ContentBentoBox';
import ProductBentoBox from './components/ProductBentoBox';
import { ProductBentoBoxTile } from './components/ProductBentoBox/types/ProductBentoBoxTile';
import { ContentBentoBoxTile } from './components/ContentBentoBox/types/ContentBentoBoxTile';
import './styles.css';
import TextImageBentoBox from './components/TextImageBentoBox';
import { TextImageBentoBoxTile } from './components/TextImageBentoBox/types/TextImageBentoBoxTile';

export default function Bento({
  tile,
  trackingPrefix,
}: Readonly<{
  tile: BentoTile;
  trackingPrefix?: string;
}>) {
  const rows = tile.grid?.grids?.[0].rows;
  const [rowsCount, setRowsCount] = React.useState(Object.keys(rows || 0).length);
  const dontCollapseOnMobile = tile['dont-collapse-on-mobile']?.value;

  if (!rows) {
    return null;
  }
  // get number of columns and rows by adding together all colspans and rowspans from the first row
  const columnsCount = rows[0]?.columns?.reduce(
    (acc, column) => acc + (column?.layout?.colspan || 0),
    0,
  );

  // when the screen width is smaller than 768px, set the rowsCount to be all rowspans counted together
  // so that there is row for every cell since they are all on top of each other
  useEffect(() => {
    if (window.innerWidth < 768 && !dontCollapseOnMobile) {
      const allRows = rows.reduce(
        (acc, row) =>
          acc + row.columns.reduce((acc, column) => acc + (column.layout?.rowspan || 0), 0),
        0,
      );
      setRowsCount(allRows);
    }
  }, []);

  return (
    <Container>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${columnsCount}, 1fr)`,
          gridTemplateRows: `repeat(${rowsCount}, 100px)`,
        }}
        className={twJoin('gap-4 lg:gap-8', !dontCollapseOnMobile && 'bento-grid')}
      >
        {rows.map((row, rowIndex: number) => (
          <React.Fragment key={rowIndex}>
            {row.columns.map((column, columnIndex: number) => {
              const choiceComponent = componentByTypename(
                column.item?.components?.[0]?.content,
                'ComponentChoiceContent',
              );
              const choiceId = choiceComponent?.selectedComponent.id;
              const pieceComponent = componentByTypename(
                choiceComponent?.selectedComponent.content,
                'PieceContent',
              );

              return (
                <div
                  key={columnIndex + 19}
                  style={{
                    gridColumn: `span ${column.layout?.colspan}`,
                    gridRow: `span ${column.layout?.rowspan}`,
                  }}
                  className={twJoin(
                    'relative overflow-hidden rounded-xl',
                    !dontCollapseOnMobile && 'bento-grid-cell',
                  )}
                >
                  {choiceId === 'normal-bento-box' && (
                    <ContentBentoBox
                      tile={parseComponents(pieceComponent?.components) as ContentBentoBoxTile}
                      trackingPrefix={trackingPrefix}
                      // columnsCount={columnsCount}
                      // colSpan={column.layout?.colspan || 1}
                    />
                  )}
                  {choiceId === 'product-bento-box' && (
                    <ProductBentoBox
                      tile={parseComponents(pieceComponent?.components) as ProductBentoBoxTile}
                      trackingPrefix={trackingPrefix}
                    />
                  )}
                  {choiceId === 'text-image-bento-box' && (
                    <TextImageBentoBox
                      tile={parseComponents(pieceComponent?.components) as TextImageBentoBoxTile}
                      trackingPrefix={trackingPrefix}
                    />
                  )}
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </Container>
  );
}
